import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PWAUpdatePrompt() {
  const { t } = useTranslation();
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleUpdate = () => {
      navigator.serviceWorker.ready.then((registration) => {
        // Check if there's a waiting service worker
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          showUpdateToast(registration.waiting);
        }

        // Listen for new service workers
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              setWaitingWorker(newWorker);
              showUpdateToast(newWorker);
            }
          });
        });
      });

      // Detect controller change and reload
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
      });
    };

    handleUpdate();
  }, []);

  const showUpdateToast = (worker: ServiceWorker) => {
    toast(
      <div className="flex items-center gap-3">
        <RefreshCw className="h-5 w-5 text-primary animate-spin" />
        <div className="flex-1">
          <p className="font-medium text-sm">
            {t("pwa.update.title", "New version available")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("pwa.update.description", "Refresh to get the latest features")}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            worker.postMessage({ type: "SKIP_WAITING" });
            toast.dismiss();
          }}
          className="shrink-0"
        >
          {t("pwa.update.refresh", "Refresh")}
        </Button>
      </div>,
      {
        duration: Infinity,
        id: "pwa-update",
        dismissible: true,
      }
    );
  };

  return null;
}
